import { useState, useCallback, memo, useRef } from 'react';
import PhoneHeader, { BottomTabBar } from './PhoneHeader';
import TabContent from './TabContent';
import Onboarding from './Onboarding';
import { useScrollManagement, useWheelHandler, useScrollReset } from '../hooks/useScrollManagement';

export const PhoneMockup = memo(() => {
  const [showOnboarding, setShowOnboarding] = useState(true);
  const [activeTab, setActiveTab] = useState('feed');
  const [highlightedClipId, setHighlightedClipId] = useState(null);
  const [isAddingClip, setIsAddingClip] = useState(false);
  const [isClipDetailOpen, setIsClipDetailOpen] = useState(false);
  const scrollContainerRef = useRef(null);

  // Use custom hooks for scroll management
  const { handleMouseEnter, handleMouseLeave } = useScrollManagement();
  const handleWheel = useWheelHandler(scrollContainerRef);
  useScrollReset(scrollContainerRef, activeTab);

  const handleClipClick = useCallback((clipId) => {
    setActiveTab('clipboard');
    setHighlightedClipId(clipId);
    setTimeout(() => setHighlightedClipId(null), 2000);
  }, []);

  const handleAddClick = useCallback(() => {
    setActiveTab('clipboard');
    setIsAddingClip(true);
  }, []);

  const tabBackground = '#efece4';

  return (
    <div className="relative mx-auto max-w-sm">
      {/* Phone Frame */}
      <div 
        className="relative w-full aspect-[9/19] bg-gradient-to-br from-gray-400 via-gray-500 to-gray-600 rounded-[3rem] p-3 shadow-2xl border border-gray-300"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onWheel={handleWheel}
      >
        {/* Screen - transform creates containing block for fixed descendants; id for modal portal */}
        <div 
          id="phone-screen"
          className="relative w-full h-full rounded-[2.25rem] overflow-hidden bg-[#efece4]"
          style={{ transform: 'translateZ(0)' }}
        >
          {/* Fixed Header */}
          <div className="absolute top-0 left-0 right-0 z-20">
            <PhoneHeader onProfileClick={() => setActiveTab('profile')} />
          </div>

          {/* Scrollable Content Area. Top 56px matches header height. */}
          <div 
            ref={scrollContainerRef}
            className={`absolute inset-0 overflow-y-scroll overflow-x-hidden pt-[56px] pb-[60px] ${isClipDetailOpen ? 'z-40' : ''}`}
            style={{ 
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'thin',
              backgroundColor: tabBackground,
            }}
          >
            <TabContent 
              activeTab={activeTab} 
              onClipClick={handleClipClick} 
              highlightedClipId={highlightedClipId}
              isAddingClip={isAddingClip}
              setIsAddingClip={setIsAddingClip}
              onClipDetailOpenChange={setIsClipDetailOpen}
            />
          </div>

          {/* Bottom Navigation Bar */}
          <div className="absolute bottom-0 left-0 right-0 z-30">
            <BottomTabBar
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              onAddClick={handleAddClick}
            />
          </div>

          {/* Onboarding overlay — must be absolute (not fixed) because #phone-screen has transform */}
          {showOnboarding && (
            <div className="absolute inset-0 z-50">
              <Onboarding onComplete={() => setShowOnboarding(false)} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

PhoneMockup.displayName = 'PhoneMockup';

// PhoneMockup has no props, so no PropTypes needed
